// Base redux functions.
import {
    SliceFactory,
    FetchCollectionData,
    FetchItemDataType,
    ApiCollectionResponse,
} from '@europrocurement/l2d-redux-utils';

// Api configuration.
import {
    JointureUtilisateurGroupeTravailApi,
    JointureUtilisateurGroupeTravailApiApiUtilisateurgroupetravailGetCollectionRequest as UtilisateurGroupeTravailRequestParams,
    Configuration as UtilisateursConfiguration,
} from '../../../openApi/ApiUtilisateur';

/**
 * Types.
 * Import mutable type, entity object interface and API configuration parameters.
 */
import {
    type Mutable,
    type UtilisateurGroupeTravailUtilisateurApiObject,
    type UtilisateursConfigurationParameters,
} from '../types';

// Constants.
import {
    UTILISATEUR_REDUCER_NAME,
    UTILISATEUR_GROUPE_TRAVAIL_SLICE_NAME,
    GROUPE_TRAVAIL_ACTIVE_INSCRIPTION_DATASOURCE_NAME,
} from '../constants';

export type UtilisateurGroupeTravail = UtilisateurGroupeTravailUtilisateurApiObject;

/**
 * Mapper.
 *
 * Function that transform Api objects into front compatible ones (DTO).
 * Mandatory for createSlice().
 */
const mapperUtilisateurGroupeTravail = function (inscription: UtilisateurGroupeTravail) {
    return {
        ...inscription,
    } as UtilisateurGroupeTravail;
};

/**
 * Function that fetch the collection of groupesTravail and store it into the global state Redux
 * @param configuration API configuration parameters
 * @returns the response of API request (get collection/get by id)
 */
export function createUtilisateurGroupeTravailSlice(
    configuration: UtilisateursConfigurationParameters,
) {
    // Create an Api utilisateurs configuration with interface.
    const conf = new UtilisateursConfiguration(configuration);
    const api = new JointureUtilisateurGroupeTravailApi(conf);

    // Create getCollection callback.
    const fetchUtilisateurGroupeTravailCollection: FetchCollectionData<
        UtilisateurGroupeTravail
    > = ({ pagination: { page, itemsPerPage }, filters }) => {
        // Api groupesTravail getCollection Request Parameters (filters, search, orders...).
        const requestParameters: Mutable<UtilisateurGroupeTravailRequestParams> = {
            page: page + 1,
            itemsPerPage,
        };

        // Search by workgroup id or iri
        if (filters.groupetravail !== undefined) {
            requestParameters.groupetravail = filters.groupetravail as string;
        }

        // Search by user id or iri
        if (filters.user !== undefined) {
            requestParameters.user = filters.user as string;
        }

        /**
         * Manage boolean filters.
         */
        if (filters.historicInscription !== undefined) {
            requestParameters.historicInscription = filters.historicInscription as boolean;
        }

        return api
            .apiUtilisateurgroupetravailGetCollection(requestParameters)
            .then((res) => res.data)
            .catch((error) => {
                console.error(error);
            }) as Promise<ApiCollectionResponse<UtilisateurGroupeTravail>>;
    };

    const fetchUtilisateurgroupetravailItem: FetchItemDataType<UtilisateurGroupeTravail> = ({
        idItem,
        // pagination,
    }) =>
        api
            .apiUtilisateurgroupetravailIdGet({ id: String(idItem) })
            .then((res) => res.data) as Promise<UtilisateurGroupeTravail>;

    const {
        slice: utilisateurGroupeTravailSlice,
        dataSourcesThunks: utilisateurGroupeTravailDataSourcesThunks,
    } = SliceFactory.createSlice<UtilisateurGroupeTravail, UtilisateurGroupeTravail>(
        UTILISATEUR_GROUPE_TRAVAIL_SLICE_NAME,
        UTILISATEUR_REDUCER_NAME,
        [GROUPE_TRAVAIL_ACTIVE_INSCRIPTION_DATASOURCE_NAME],
        fetchUtilisateurGroupeTravailCollection,
        fetchUtilisateurgroupetravailItem,
        mapperUtilisateurGroupeTravail,
        {
            historicInscription: false,
        },
    );

    return { utilisateurGroupeTravailSlice, utilisateurGroupeTravailDataSourcesThunks };
}
